import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";
import NavigationMenu from "../../components/NavigationMenu";
import HeroSection from "../../components/HeroSection";
import FeaturesSection from "../../components/FeaturesSection";
import TestimonialsSection from "../../components/TestimonialsSection";
import CTASection from "../../components/CTASection";
import FooterSection from "../../components/FooterSection";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
  },
  previewBanner: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    boxShadow: theme.shadows[4],
  },
  content: {
    paddingTop: 56, // Height of preview banner
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  error: {
    padding: theme.spacing(3),
    textAlign: "center",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const PreviewLandingPage = () => {
  const classes = useStyles();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDraft();
    
    // Poll for changes every 5 seconds
    const interval = setInterval(() => {
      fetchDraft(true);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const fetchDraft = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await api.get("/landing-page/draft");
      setContent(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching draft:", err);
      if (!silent) {
        setError("Error al cargar la vista previa");
        toast.error("Error al cargar el contenido");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const renderSection = (section) => {
    if (!section.active) return null;

    switch (section.type) {
      case "navigation":
        return (
          <NavigationMenu
            key={section.id}
            logo={section.data.logo}
            links={section.data.links}
            ctaButton={section.data.ctaButton}
            sticky={section.data.sticky}
          />
        );
      case "hero":
        return (
          <HeroSection
            key={section.id}
            title={section.data.title}
            subtitle={section.data.subtitle}
            backgroundImage={section.data.backgroundImage}
            cta={section.data.cta}
          />
        );
      case "features":
        return (
          <FeaturesSection
            key={section.id}
            title={section.data.title}
            features={section.data.features}
          />
        );
      case "testimonials":
        return (
          <TestimonialsSection
            key={section.id}
            title={section.data.title}
            testimonials={section.data.testimonials}
          />
        );
      case "cta":
        return (
          <CTASection
            key={section.id}
            title={section.data.title}
            description={section.data.description}
            primaryCta={section.data.primaryCta}
            secondaryCta={section.data.secondaryCta}
          />
        );
      case "footer":
        return (
          <FooterSection
            key={section.id}
            logo={section.data.logo}
            columns={section.data.columns}
            socialLinks={section.data.socialLinks}
            copyright={section.data.copyright}
            bottomLinks={section.data.bottomLinks}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.error}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!content || !content.sections) {
    return (
      <Box className={classes.error}>
        <Typography variant="h6">
          No hay contenido para previsualizar
        </Typography>
      </Box>
    );
  }

  const activeSections = content.sections
    .filter((section) => section.active)
    .sort((a, b) => a.order - b.order);

  return (
    <Box className={classes.root}>
      <Paper className={classes.previewBanner} elevation={4}>
        <Visibility />
        <Typography variant="body1" style={{ fontWeight: 600 }}>
          VISTA PREVIA
        </Typography>
        <Chip
          label="Cambios no publicados"
          size="small"
          style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
        />
      </Paper>
      <Box className={classes.content}>
        {activeSections.map((section) => renderSection(section))}
      </Box>
    </Box>
  );
};

export default PreviewLandingPage;
